import React, { useEffect, useMemo } from "react";
import Stack from "@mui/material/Stack";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";
import ComboBox from "../../ComboBox";
import PageSearch from "../../../admin/PageSearch";
import { createAutocompleteOptions } from "../../../utility/utils";
import { fetchAllSbusofSbg } from "../../../redux/actions/UserHierarchyActions";

interface IFilterBarSection {
  filters: {
    search: string;
    sbuHead: string;
    bdHead: string;
    sbgHead: string;
    bdPoc: string;
  };
  onChange: (key: string, value: string) => void;
}

interface FilterProps {
  value: string;
  onChange: (key: string, value: string) => void;
  onChangeSbgValue?: any;
  selectedSbg?: string;
}

export const SBGFilter = ({
  value,
  onChange,
  onChangeSbgValue,
}: FilterProps) => {
  const allSbgheads = useAppSelector((state) => state.getUserName.all_sbgheads);

  const allSbgHeadOptions = useMemo(() => {
    const sbgHeadOptions = createAutocompleteOptions(
      allSbgheads,
      "departmentName",
      "departmentName"
    );
    sbgHeadOptions.unshift({
      value: "all",
      label: "All",
    });
    return sbgHeadOptions;
  }, [allSbgheads]);

  const handleOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    onChange("sbgHead", value);
    onChangeSbgValue(value);
    onChange("sbuHead", "all");
  };

  return (
    <ComboBox
      options={allSbgHeadOptions}
      disableClearable
      value={value}
      label="SBG"
      placeholder="SBG"
      onChange={handleOnChange}
    />
  );
};

export const SBUFilter = ({ value, onChange, selectedSbg }: FilterProps) => {
  const dispatch = useAppDispatch();
  const allSubheads = useAppSelector((state) => state.getUserName.all_sbuheads);
  useEffect(() => {
    if (selectedSbg) {
      dispatch(fetchAllSbusofSbg(selectedSbg));
    }
  }, [selectedSbg, dispatch]);

  const sbus = useAppSelector(
    (state) => state.UserHierarchy.all_sbus_uder_sbgs
  );
  const sbuOptions = sbus && sbus.length === 0 ? allSubheads : sbus;

  const allSbuHeadOptions = useMemo(() => {
    const sbuHeadOptions = createAutocompleteOptions(
      sbuOptions,
      "departmentName",
      "departmentName"
    );
    sbuHeadOptions.unshift({
      value: "all",
      label: "All",
    });
    return sbuHeadOptions;
  }, [sbuOptions]);

  const handleOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    onChange("sbuHead", value);
  };

  return (
    <ComboBox
      options={allSbuHeadOptions}
      disableClearable
      value={value}
      label="SBU"
      placeholder="SBU"
      onChange={handleOnChange}
    />
  );
};

export const BDHeadFilter = ({ value, onChange }: FilterProps) => {
  const allBdheads = useAppSelector((state) => state.getUserName.all_bdheads);

  const allBdHeadOptions = useMemo(() => {
    const bdHeadOptions = createAutocompleteOptions(
      allBdheads,
      "userName",
      "userName"
    );
    bdHeadOptions.unshift({
      value: "all",
      label: "All",
    });
    return bdHeadOptions;
  }, [allBdheads]);

  const handleOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    onChange("bdHead", value);
  };

  return (
    <ComboBox
      options={allBdHeadOptions}
      disableClearable
      value={value}
      label="BD Head"
      placeholder="BD Head"
      onChange={handleOnChange}
    />
  );
};

export const BDPOCFilter = ({ value, onChange }: FilterProps) => {
  const allBdpocs = useAppSelector((state) => state.getUserName.all_bdpocs);

  const allBdPocOptions = useMemo(() => {
    const bdPocOptions = createAutocompleteOptions(
      allBdpocs,
      "userName",
      "userName"
    );
    bdPocOptions.unshift({
      value: "all",
      label: "All",
    });
    return bdPocOptions;
  }, [allBdpocs]);

  const handleOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    onChange("bdPoc", value);
  };

  return (
    <ComboBox
      options={allBdPocOptions}
      disableClearable
      value={value}
      label="BD POC"
      placeholder="BD POC"
      onChange={handleOnChange}
    />
  );
};

const FilterBarSection = ({ filters, onChange }: IFilterBarSection) => {
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);

  const handleSearchChange = (val: string) => {
    onChange("search", val);
  };

  return (
    <Stack spacing={1}>
      <PageSearch
        placeholder="Search project name"
        searchHandler={handleSearchChange}
        disableMb={true}
      />
      {loggedInUser.roleId === 2 ? (
        <BDPOCFilter value={filters.bdPoc} onChange={onChange} />
      ) : loggedInUser.roleId === 3 ? (
        <>
          <BDHeadFilter value={filters.bdHead} onChange={onChange} />
          <BDPOCFilter value={filters.bdPoc} onChange={onChange} />
        </>
      ) : loggedInUser.roleId === 4 ? (
        <>
          <SBUFilter value={filters.sbuHead} onChange={onChange} />
          <BDHeadFilter value={filters.bdHead} onChange={onChange} />
          <BDPOCFilter value={filters.bdPoc} onChange={onChange} />
        </>
      ) : loggedInUser.roleId >= 5 ? (
        <>
          <SBGFilter value={filters.sbgHead} onChange={onChange} />
          <SBUFilter value={filters.sbuHead} onChange={onChange} />
          <BDHeadFilter value={filters.bdHead} onChange={onChange} />
          <BDPOCFilter value={filters.bdPoc} onChange={onChange} />
        </>
      ) : null}
    </Stack>
  );
};

export default FilterBarSection;
